import { useState } from "react";
import { useAppSelector } from "../../../../store";
import {
  getCurrentPeriod,
  getLongPeriodFormat,
  getNextNPeriodNumbers,
  getSelectedPerformanceYearData,
  numMonthsBetweenDates,
} from "../../../../common/util/period";
import defaultContent from "../../../../content/actionReportGroupSop";
import { useTranslation } from "react-i18next";
import { storedLocale } from "../../../Authentication/util";
import "./OverviewCard.scss";
import { IconArrowLink } from "../../../../common/icons/IconArrowLink/IconArrowLink";
import { TrendIndicator, TrendIndicatorProps } from "../../../../common/components/TrendIndicator/TrendIndicator";

export type SopMonthData = {
  period: number;
  currentPV: string;
  expectedPV: string;
  ordersUpForRenewal: string;
  downlinesOnSOPTotal: {
    value: string;
    trend?: TrendIndicatorProps;
  };
  downlinesOnSOPMonth: string;
  downlinesNotOnSopThisMonth: string;
  groupPercent: string;
};

type props = {
  sopMonths: SopMonthData[];
};

const MetricItem = ({
  title,
  value,
  active,
  trend,
}: {
  title: string;
  value: string;
  active?: boolean;
  trend?: TrendIndicatorProps;
}) => {
  return (
    <div className={`GroupSopOverview__metricItem ${active ? "GroupSopOverview__metricItem--active" : ""}`}>
      <div className="GroupSopOverview__metricTitle">{title}</div>
      <div className="GroupSopOverview__metricValue">
        {trend && <TrendIndicator trend={trend.trend} difference={trend.difference} />}
        {value}
      </div>
    </div>
  );
};

const getPeriodList = (availableMonths: string[]) => {
  const currentPeriod = getCurrentPeriod();
  const { selectedPYStartPeriod: previousPYStartPeriod } = getSelectedPerformanceYearData(currentPeriod, 1);

  const next5FutureMonths = getNextNPeriodNumbers(currentPeriod, 5);
  const monthsInRange = numMonthsBetweenDates(
    previousPYStartPeriod,
    next5FutureMonths[next5FutureMonths.length - 1].toString(),
  );
  const periodList = getNextNPeriodNumbers(previousPYStartPeriod, monthsInRange, true).filter(
    (period) => availableMonths.includes(period.toString()) || next5FutureMonths.includes(period),
  );

  return periodList;
};

export const GroupSopOverview = ({ sopMonths }: props) => {
  const { t } = useTranslation(["actionReportGroupSop"]);

  const monthlyPeriods = useAppSelector((state) => state.boot.shellConfig.periods?.monthlyPeriods ?? []);
  const currentPeriodNumber = Number(getCurrentPeriod());
  const periodList = getPeriodList(monthlyPeriods);

  const [selectedPeriod, setSelectedPeriod] = useState<number>(currentPeriodNumber);
  const activePeriod = periodList.includes(selectedPeriod) ? selectedPeriod : currentPeriodNumber;
  const selectedIdx = periodList.indexOf(activePeriod);
  const isPastPeriod = activePeriod < currentPeriodNumber;
  const isFuturePeriod = activePeriod > currentPeriodNumber;

  const selectedMonthData = sopMonths.find((data) => data.period === activePeriod) || {
    period: activePeriod ?? 0,
    currentPV: "0",
    expectedPV: "0",
    ordersUpForRenewal: "0",
    downlinesOnSOPTotal: {
      value: "0",
    },
    downlinesOnSOPMonth: "0",
    downlinesNotOnSopThisMonth: "0",
    groupPercent: "0",
  };

  const prevPeriod = () => {
    if (selectedIdx > 0) {
      setSelectedPeriod(periodList[selectedIdx - 1]);
    }
  };

  const nextPeriod = () => {
    if (selectedIdx >= 0 && selectedIdx < periodList.length - 1) {
      setSelectedPeriod(periodList[selectedIdx + 1]);
    }
  };

  return (
    <section className="GroupSopOverview">
      <div className="GroupSopOverview__header">
        <div className="GroupSopOverview__headerItem">
          <span className="GroupSopOverview__headerTitle">
            {t("overviewCard.currentSopPv", defaultContent.overviewCard.currentSopPv)}
          </span>
          <span className="GroupSopOverview__headerValue">{isFuturePeriod ? "--" : selectedMonthData.currentPV}</span>
        </div>

        <div className="GroupSopOverview__headerItem">
          <span className="GroupSopOverview__headerTitle">
            {t("overviewCard.expectedSopPv", defaultContent.overviewCard.expectedSopPv)}
          </span>
          <span className="GroupSopOverview__headerValue">{isPastPeriod ? "--" : selectedMonthData.expectedPV}</span>
        </div>
      </div>

      <div className="GroupSopOverview__metricList">
        <MetricItem
          title={t("overviewCard.ordersUpForRenewal", defaultContent.overviewCard.ordersUpForRenewal)}
          value={selectedMonthData.ordersUpForRenewal}
          active={true}
        />
        <MetricItem
          title={t("overviewCard.totalDownlinesOnSop", defaultContent.overviewCard.totalDownlinesOnSop)}
          value={selectedMonthData.downlinesOnSOPTotal.value}
          trend={selectedMonthData.downlinesOnSOPTotal.trend}
        />
        <MetricItem
          title={t("overviewCard.genSopPvThisMonth", defaultContent.overviewCard.genSopPvThisMonth)}
          value={selectedMonthData.downlinesOnSOPMonth}
        />
        <MetricItem
          title={t("overviewCard.notGenSopPvThisMonth", defaultContent.overviewCard.notGenSopPvThisMonth)}
          value={selectedMonthData.downlinesNotOnSopThisMonth}
        />
        <MetricItem
          title={t("overviewCard.percentGroupOnSop", defaultContent.overviewCard.percentGroupOnSop)}
          value={selectedMonthData.groupPercent}
        />
      </div>

      <div className="GroupSopOverview__monthSelector">
        <button className="GroupSopOverview__monthStepperBtn" onClick={prevPeriod} disabled={selectedIdx <= 0}>
          <IconArrowLink iconColor={selectedIdx <= 0 ? "gray" : "blue"} />{" "}
        </button>
        <span className="GroupSopOverview__selectedMonth">{getLongPeriodFormat(activePeriod, storedLocale())}</span>
        <button
          className="GroupSopOverview__monthStepperBtn"
          onClick={nextPeriod}
          disabled={selectedIdx < 0 || selectedIdx >= periodList.length - 1}
        >
          <IconArrowLink iconColor={selectedIdx < 0 || selectedIdx >= periodList.length - 1 ? "gray" : "blue"} />{" "}
        </button>
      </div>
    </section>
  );
};
